import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DOCUSIGN_TRIGGER_IDS, TriggerIds } from '../actionConfigs/shared-action-configurations';

export interface IBullhornNoteAction {
  id: string;
  type: string;
  properties: {
    namespace: string;
    fields: {
      action: string;
      comments: string;
    };
  };
}

@Component({
    selector: 'app-bullhorn-create-note',
    templateUrl: './bullhorn-create-note.component.html',
    styleUrls: ['./bullhorn-create-note.component.scss'],
    standalone: false
})
export class BullhornCreateNoteComponent {
  static NAME = 'create-note';
  private static readonly DOCUSIGN_DEFAULT_COMMENTS =
    '<a href="[(${signature.sentResponse.properties.viewLink})]" target="_blank">Open in Docusign</a>';

  private static readonly DOCUMENT_SAVE_DEFAULT_COMMENTS =
    'File [(${documentSaveNote.uploadResults[0].fileName})][(${documentSaveNote.outputFileType != null and !documentSaveNote.outputFileType.isEmpty() ? \' with type \' + documentSaveNote.outputFileType : \'\'})] ' +
    'created for [(${documentSaveNote.source.type})] record [(${documentSaveNote.source.name})] ([(${documentSaveNote.source.id})]) ' +
    'and saved against [(${documentSaveNote.destinationsSummary})] by user [(${documentSaveNote.user.name})] ([(${documentSaveNote.user.id})]).';

  @Input() formGroup: FormGroup;
  @Input() triggerId = '';
  @Output() delete: EventEmitter<void> = new EventEmitter();

  get isDocusignAction(): boolean {
    return DOCUSIGN_TRIGGER_IDS.includes(this.triggerId as typeof DOCUSIGN_TRIGGER_IDS[number]);
  }

  get isSaveToEntityAction(): boolean {
    return this.triggerId === TriggerIds.SAVE_TO_ENTITY;
  }

  static createFormGroup(action?: IBullhornNoteAction, triggerId?: string) {
    action = BullhornCreateNoteComponent.create(action, triggerId);
    return new FormGroup({
      id: new FormControl(action.id, Validators.required),
      type: new FormControl(action.type, Validators.required),
      properties: new FormGroup({
        namespace: new FormControl(action.properties?.namespace, Validators.required),
        fields: new FormGroup({
          action: new FormControl(action.properties?.fields?.action, [Validators.required, Validators.maxLength(30)]),
          comments: new FormControl(action.properties?.fields?.comments, Validators.required),
        }),
      }),
    });
  }

  static isAction(action: any): boolean {
    return action.id.endsWith(this.NAME);
  }

  static create(existing?: IBullhornNoteAction, triggerId?: string): IBullhornNoteAction {
    const isDocusign = DOCUSIGN_TRIGGER_IDS.includes(triggerId as typeof DOCUSIGN_TRIGGER_IDS[number]);
    const defaultComments = isDocusign ? BullhornCreateNoteComponent.DOCUSIGN_DEFAULT_COMMENTS
      : BullhornCreateNoteComponent.DOCUMENT_SAVE_DEFAULT_COMMENTS;
    return {
      // CreateNoteTaskConfig
      id: existing?.id ?? `bullhorn.${BullhornCreateNoteComponent.NAME}`,
      type: existing?.type ?? 'task',
      properties: {
        namespace: existing?.properties?.namespace ?? 'bullhorn',
        fields: {
          action: existing?.properties?.fields?.action,
          comments: existing?.properties?.fields?.comments ?? defaultComments,
        },
      },
    };
  }

  removeAction() {
    this.delete.emit();
  }
}
